/**
 * App → agent handoff — the symmetric inverse of `app-links.ts`. Where those
 * deep-link from an MCP result BACK into the Brew app, these hand an in-app
 * entity OUT to an external AI harness: a compact, connector-aware PROMPT the
 * agent runs against the Brew MCP, plus the prefill deep-link URLs for the
 * harnesses that support one. Pure string builders — no React/server deps.
 *
 * Only harnesses that support BOTH a prefilled-prompt URL AND Brew through a
 * plugin or MCP connection get a deep-link button — ChatGPT, Cursor, Claude Desktop. Every
 * other harness uses Copy prompt instead: Claude on the WEB no longer accepts a
 * prefill query param (it was removed in 2025 for a prompt-injection issue), and
 * Codex / Claude Code have no URL handoff at all.
 *
 * A harness's Brew connection is bound to ONE brand (chosen at OAuth consent), so
 * the prompt NAMES the target brand — the agent must be connected to that brand
 * or its by-id tool call will 403. Keep prompts compact (well under the ~2k-char
 * URL ceiling every browser/harness shares).
 */

import { mcpToolNameForOperation } from './bindings/names'

/** The clean, brand-agnostic MCP connection URL (brand rides the token/key). */
export const MCP_CONNECTION_URL = 'https://brew.new/api/mcp'

/**
 * Use the public directory listing as soon as OpenAI assigns it. Until then,
 * preserve the useful prompt-prefill handoff to a new ChatGPT conversation.
 */
function buildChatGptHandoffUrl(prompt: string): string {
  const directoryUrl =
    process.env.NEXT_PUBLIC_CHATGPT_BREW_PLUGIN_URL?.trim() ?? ''
  return directoryUrl || `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`
}

/** The in-app entity a handoff prompt is about. */
export type HandoffEntity =
  | {
      kind: 'email'
      emailId: string
      title?: string
      version?: number
      brandName?: string
    }
  | {
      kind: 'automation'
      automationId: string
      title?: string
      brandName?: string
    }
  | { kind: 'brand'; brandName?: string }
  | { kind: 'chat'; chatId: string; chatTitle?: string; brandName?: string }
  | { kind: 'generic'; brandName?: string }

/** Strip quotes/backticks/newlines so an odd entity/brand name can't break out
 *  of the prompt's quoted framing (the prompt is plain text the agent reads). */
function sanitizeForPrompt(value: string): string {
  return value
    .replace(/["`\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function namedClause(title?: string): string {
  const clean = title ? sanitizeForPrompt(title) : ''
  return clean ? ` "${clean}"` : ''
}

function brandLine(brandName?: string): string {
  const clean = brandName ? sanitizeForPrompt(brandName) : ''
  return clean
    ? `\n\nThis is the "${clean}" brand — make sure your Brew connection is authorized for that brand.`
    : ''
}

const CONNECT_HINT = `\n\nNot connected yet? In ChatGPT, install or enable the Brew plugin with server URL ${MCP_CONNECTION_URL}. In another MCP client, add that server URL to its connection settings.`

/**
 * Build the compact prompt an external agent runs against the Brew MCP to pick
 * up exactly the entity the user is looking at. Names the relevant MCP tools +
 * the entity id so the agent can act immediately.
 */
export function buildAgentPrompt(entity: HandoffEntity): string {
  switch (entity.kind) {
    case 'email': {
      const version = entity.version ? ` (version ${entity.version})` : ''
      return (
        `Help me work on my Brew email design${namedClause(entity.title)}.\n\n` +
        `Using the Brew plugin or MCP connection, call \`get_email\` with emailId "${entity.emailId}"${version}. The tool returns an inline preview in chat — then help me iterate on the copy, layout, and on-brand design with \`edit_email\`.` +
        brandLine(entity.brandName) +
        CONNECT_HINT
      )
    }
    case 'automation':
      return (
        `Help me improve my Brew email automation${namedClause(entity.title)}.\n\n` +
        `Using the Brew plugin or MCP connection, load automationId "${entity.automationId}" with \`get_automation\` (and \`get_automation with analytics:{}\` for its performance), then help me refine the triggers, timing, and the emails it sends — use \`save_automation\` and \`test_automation\`.` +
        brandLine(entity.brandName) +
        CONNECT_HINT
      )
    case 'chat':
      return (
        `Resume my Brew chat${namedClause(entity.chatTitle)}.\n\n` +
        `Using the Brew plugin or MCP connection, call \`get_chat_context\` with chatId "${entity.chatId}" to load this conversation's full context — what's been done and the emails, automations, brand, and audiences it references — then load those entities with the matching tools and help me continue where I left off.` +
        brandLine(entity.brandName) +
        CONNECT_HINT
      )
    case 'brand':
      return (
        'Help me run my email marketing through Brew.\n\n' +
        `Using the Brew plugin or MCP connection, call \`get_brand\` to load my brand (voice, colors, audiences), then help me plan and generate on-brand email campaigns (\`${mcpToolNameForOperation('create_email_design')}\`), build automations, and segment audiences.` +
        brandLine(entity.brandName) +
        CONNECT_HINT
      )
    case 'generic':
      return (
        'I want to run my email marketing through Brew — the AI-native ESP for modern teams and their AI agents.\n\n' +
        `In ChatGPT, install or enable the Brew plugin with server URL ${MCP_CONNECTION_URL}; in another MCP client, add that server URL. Then call \`get_brew_capabilities\` and help me generate on-brand emails, send campaigns, and build automations.` +
        brandLine(entity.brandName)
      )
  }
}

/** Harnesses that support BOTH a prefill URL AND custom MCP connectors. */
type HandoffHarnessId = 'chatgpt' | 'cursor' | 'claude-desktop'

export type HandoffHarness = {
  id: HandoffHarnessId
  name: string
  /** Logo domain — reuses `McpClientLogo` (context.dev Logo Link). */
  logoDomain: string
  /** The prefilled-prompt deep link for this harness. */
  buildUrl: (prompt: string) => string
  /** A custom URL scheme (needs the app installed; no web fallback). */
  isAppScheme?: boolean
}

export const HANDOFF_HARNESSES: ReadonlyArray<HandoffHarness> = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    logoDomain: 'openai.com',
    buildUrl: buildChatGptHandoffUrl,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    logoDomain: 'cursor.com',
    buildUrl: (prompt) =>
      `https://cursor.com/link/prompt?text=${encodeURIComponent(prompt)}`,
  },
  {
    id: 'claude-desktop',
    name: 'Claude',
    logoDomain: 'claude.com',
    buildUrl: (prompt) =>
      `claude://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    isAppScheme: true,
  },
]

/**
 * Cursor's one-click MCP-install deep link (the only harness that has one).
 * `config` is base64(JSON) of the server entry, so a user can add the Brew
 * connector to Cursor without hand-editing mcp.json. Custom scheme → needs the
 * Cursor app installed. (Browser-safe base64 via `btoa`.)
 */
export function buildCursorInstallUrl(
  connectionUrl: string = MCP_CONNECTION_URL
): string {
  const json = JSON.stringify({ url: connectionUrl })
  const base64 = btoa(json)
  // Emit URL-safe base64url (no +/=) RAW — Cursor's documented examples pass the
  // config unescaped, so percent-encoding the padding would break some parsers.
  const base64url = base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/[=]+$/, '')
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=brew&config=${base64url}`
}
